import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  Plus,
  Megaphone,
  RefreshCw,
  Loader2,
  Calendar,
  Globe,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  subscribeCampaigns,
  subscribeLeads,
  campaignPlatforms,
  collavueSyncCost,
  dailySyncState,
} from "../../services/collavueStore";
import { syncCampaign } from "../../services/collavueApi";
import { useToast } from "../../contexts/ToastProvider";
import PageHeader from "../../components/collavue/PageHeader";
import Empty from "../../components/collavue/Empty";
import SyncAnimationOverlay from "../../components/collavue/SyncAnimationOverlay";
const FILTER_STORAGE_KEY = "collavue.leads.campaignFilter";

// Pill + inline note styling per daily-sync state. "warn" means auto-sync is
// switched on but the next run won't produce leads (no credits, campaign
// paused, last run failed) — the case that used to look identical to healthy.
const SYNC_TONE = {
  on: {
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
    note: "text-emerald-700",
  },
  warn: {
    pill: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
    note: "text-amber-700",
  },
  off: {
    pill: "bg-gray-50 text-gray-400 border-gray-200 hover:text-blue-600 hover:border-blue-200",
    dot: "bg-gray-300",
    note: "text-gray-500",
  },
};

export default function CampaignsPage() {
  const { user, userProfile } = useAuth();
  const { push } = useToast();
  const nav = useNavigate();
  const credits = Number(userProfile?.credits ?? 0);
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [syncingId, setSyncingId] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [syncingCampaign, setSyncingCampaign] = useState(null);

  useEffect(() => {
    if (!user) return;
    const a = subscribeCampaigns(setCampaigns);
    const b = subscribeLeads(null, setLeads);
    return () => {
      a();
      b();
    };
  }, [user]);

  const onSync = async (id) => {
    const camp = campaigns.find((c) => c.id === id) || null;
    setSyncingCampaign(camp);
    setSyncResult(null);
    setSyncError(null);
    setSyncingId(id);
    try {
      const r = await syncCampaign(id);
      setSyncResult(r);
      push({
        title: "Sync complete",
        desc: `${r.stats?.leadsCreated || 0} new leads, ${r.stats?.hotLeads || 0} hot.`,
        variant: "success",
      });
    } catch (e) {
      setSyncError(e.message);
      push({ title: "Sync failed", desc: e.message, variant: "error" });
    } finally {
      setSyncingId(null);
    }
  };

  const closeOverlay = () => {
    const hadResult = !!syncResult;
    const target = syncingCampaign;
    setSyncingCampaign(null);
    setSyncResult(null);
    setSyncError(null);
    if (hadResult && target) {
      try {
        window.localStorage.setItem(FILTER_STORAGE_KEY, target.id);
      } catch {}
      nav(`/leads?campaignId=${target.id}`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 bg-[#fafafa] min-h-screen font-sans text-[#0B1038]">
      <SyncAnimationOverlay
        open={!!syncingCampaign}
        campaign={syncingCampaign}
        result={syncResult}
        error={syncError}
        onClose={closeOverlay}
      />
      <PageHeader
        title="Campaigns"
        subtitle="One campaign per offer or audience — keep them focused, keep them performing."
        actions={
          <Link
            to="/campaigns/new"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#0B1038] text-white text-sm font-semibold hover:bg-[#151c54] transition-colors"
          >
            <Plus className="w-4 h-4" /> New campaign
          </Link>
        }
      />

      {campaigns.length === 0 ? (
        <Empty
          icon={Megaphone}
          title="No campaigns yet"
          body="Each campaign tracks a specific offer or audience. Create your first one in under a minute."
          cta={
            <Link
              to="/campaigns/new"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#0B1038] text-white font-semibold hover:bg-[#151c54]"
            >
              <Plus className="w-4 h-4" /> Create campaign
            </Link>
          }
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {campaigns.map((c) => {
            const cLeads = leads.filter((l) => l.campaignId === c.id);
            const hot = cLeads.filter((l) => l.category === "hot").length;
            const sync = dailySyncState(c, {
              credits,
              autoSyncEnabled: userProfile?.autoSyncEnabled,
            });
            const tone = SYNC_TONE[sync.tone] || SYNC_TONE.off;
            return (
              <div
                key={c.id}
                className="bg-white border border-gray-200 rounded-md p-5 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <button
                    onClick={() => nav(`/campaigns/${c.id}`)}
                    className="text-left min-w-0"
                  >
                    <h3 className="font-display text-xl text-[#0B1038] tracking-tight truncate">
                      {c.name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {c.companyName} · {c.industry || "General"}
                      {c.campaignType === "freelancer" ? " · Freelance" : ""}
                    </p>
                  </button>
                  <span className="flex flex-col items-end gap-1 shrink-0">
                    <Link
                      to={`/campaigns/${c.id}`}
                      title={sync.detail}
                      className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wide uppercase px-2 py-0.5 rounded-sm border ${tone.pill}`}
                    >
                      <span className="relative flex w-2 h-2">
                        {sync.tone === "on" && (
                          <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                        )}
                        <span
                          className={`relative inline-flex w-2 h-2 rounded-full ${tone.dot}`}
                        />
                      </span>
                      {sync.label}
                    </Link>
                  </span>
                </div>

                {sync.enabled && (
                  <p className={`mt-2 text-xs leading-relaxed ${tone.note}`}>
                    {sync.detail}
                  </p>
                )}

                <dl className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <Stat label="Leads" value={cLeads.length} />
                  <Stat label="Hot" value={hot} accent="text-rose-600" />
                  <Stat label="Window" value={`${c.dateRangeDays || 7}d`} />
                </dl>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {c.lastSyncAt?.toDate
                      ? `Last synced ${c.lastSyncAt.toDate().toLocaleString()}`
                      : "Never synced"}
                  </span>
                  {c.websiteUrl && (
                    <a
                      href={
                        c.websiteUrl.startsWith("http")
                          ? c.websiteUrl
                          : `https://${c.websiteUrl}`
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 hover:text-blue-600"
                    >
                      <Globe className="w-3.5 h-3.5" /> Site
                    </a>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => onSync(c.id)}
                    disabled={syncingId === c.id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0B1038] text-white text-sm font-semibold hover:bg-[#151c54] disabled:opacity-60"
                  >
                    {syncingId === c.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                    Sync ({collavueSyncCost(c.dateRangeDays, campaignPlatforms(c))})
                  </button>
                  <Link
                    to={`/leads?campaignId=${c.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-gray-300 text-sm hover:bg-gray-50"
                  >
                    View leads
                  </Link>
                  <Link
                    to={`/campaigns/${c.id}`}
                    className="ml-auto inline-flex items-center text-sm text-blue-600 hover:text-[#0B1038] font-medium"
                  >
                    Manage <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-sm px-2 py-1.5">
      <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">
        {label}
      </div>
      <div className={`text-base font-bold tabular-nums ${accent || "text-[#0B1038]"}`}>
        {value}
      </div>
    </div>
  );
}
